import React, { useCallback } from "react";
import { SUPERMARKET_BRANDS } from "../../data/supermarkets";
import { CommuteMode } from "../../util/tflFareHeuristic";
import { isValidUkPostcode, normalizeUkPostcode } from "../../util/ukPostcode";
import {
  canPlanMyDay,
  CostOfLivingFormState,
  DayType,
  ExtraStop,
} from "./types";

export interface CostOfLivingFormProps {
  propertyLon: number;
  propertyLat: number;
  form: CostOfLivingFormState;
  onChange: (next: CostOfLivingFormState) => void;
  onPlanMyDay: () => void;
  isPlanning: boolean;
}

const COMMUTE_MODES: { value: CommuteMode; label: string }[] = [
  { value: "tube", label: "Tube" },
  { value: "bus", label: "Bus" },
  { value: "walk", label: "Walk" },
  { value: "cycle", label: "Cycle" },
  { value: "drive", label: "Drive" },
  { value: "mixed", label: "Mixed" },
];

const DAY_TYPES: { value: DayType; label: string; description: string }[] = [
  { value: "work", label: "Work", description: "Commute to office" },
  { value: "student", label: "Student", description: "Commute to school" },
  { value: "parent", label: "Parent", description: "School run + optional work" },
];

function primaryPostcodeLabel(dayType: DayType): string {
  if (dayType === "student" || dayType === "parent") return "School postcode";
  return "Office postcode";
}

const PostcodeInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => {
  const trimmed = value.trim();
  const valid = trimmed.length > 0 && isValidUkPostcode(value);
  const invalid = trimmed.length > 0 && !isValidUkPostcode(value);

  return (
    <div>
      <label className="block font-manrope text-sm text-text-slate mb-2">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full p-3 border border-solid font-manrope ${
          valid
            ? "border-green-500"
            : invalid
              ? "border-red-400"
              : "border-[rgba(0,0,0,0.1)]"
        }`}
        placeholder="e.g. W1J 8DZ"
        autoComplete="postal-code"
      />
      {valid && (
        <p className="text-xs text-green-700 mt-1">
          ✓ {normalizeUkPostcode(value)}
        </p>
      )}
      {invalid && (
        <p className="text-xs text-red-600 mt-1">
          Enter a valid UK postcode (e.g. W1J 8DZ).
        </p>
      )}
    </div>
  );
};

const CostOfLivingForm: React.FC<CostOfLivingFormProps> = ({
  form,
  onChange,
  onPlanMyDay,
  isPlanning,
}) => {
  const update = useCallback(
    (patch: Partial<CostOfLivingFormState>) => {
      onChange({ ...form, ...patch });
    },
    [form, onChange]
  );

  const handleDayTypeChange = (dayType: DayType) => {
    const patch: Partial<CostOfLivingFormState> = { dayType };
    if (dayType === "parent" && form.arriveByTime === "09:00") {
      patch.schoolDropoffTime = "08:30";
    }
    update(patch);
  };

  const updateExtraStop = (index: number, patch: Partial<ExtraStop>) => {
    const extraStops = form.extraStops.map((stop, i) =>
      i === index ? { ...stop, ...patch } : stop
    );
    update({ extraStops });
  };

  const addExtraStop = () => {
    if (form.extraStops.length >= 2) return;
    update({ extraStops: [...form.extraStops, { label: "", postcode: "" }] });
  };

  const removeExtraStop = (index: number) => {
    if (form.extraStops.length <= 1) {
      update({ extraStops: [{ label: "", postcode: "" }] });
      return;
    }
    update({ extraStops: form.extraStops.filter((_, i) => i !== index) });
  };

  const toggleSupermarket = (id: string) => {
    const selected = form.selectedSupermarketIds.includes(id)
      ? form.selectedSupermarketIds.filter((x) => x !== id)
      : [...form.selectedSupermarketIds, id];
    update({ selectedSupermarketIds: selected.length ? selected : [id] });
  };

  const planEnabled = canPlanMyDay(form);

  return (
    <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6">
      <div className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-2">
        Cost of Living
      </div>
      <h3 className="font-teko text-2xl text-text-obsidian mb-4">
        Plan your day from this home
      </h3>

      <div className="space-y-8">
        <section>
          <h4 className="font-teko text-xl mb-4">Your day</h4>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {DAY_TYPES.map((dt) => (
              <button
                key={dt.value}
                type="button"
                onClick={() => handleDayTypeChange(dt.value)}
                className={`p-4 border border-solid text-left transition ${
                  form.dayType === dt.value
                    ? "border-brand-yellow bg-bg-porcelain"
                    : "border-[rgba(0,0,0,0.08)] hover:border-[rgba(0,0,0,0.15)]"
                }`}
              >
                <div className="font-teko text-lg">{dt.label}</div>
                <div className="text-xs text-text-grey mt-1">{dt.description}</div>
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <PostcodeInput
              label={primaryPostcodeLabel(form.dayType)}
              value={form.primaryPostcode}
              onChange={(v) => update({ primaryPostcode: v })}
            />

            {form.dayType === "parent" && (
              <PostcodeInput
                label="Work postcode (after drop-off, optional)"
                value={form.workPostcode}
                onChange={(v) => update({ workPostcode: v })}
              />
            )}

            <div>
              <label className="block font-manrope text-sm text-text-slate mb-2">
                {form.dayType === "parent"
                  ? "Arrive at school by"
                  : form.dayType === "student"
                    ? "Arrive at school by"
                    : "Arrive at office by"}
              </label>
              <input
                type="time"
                value={form.arriveByTime}
                onChange={(e) => update({ arriveByTime: e.target.value })}
                className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope"
              />
            </div>

            <div>
              <label className="block font-manrope text-sm text-text-slate mb-2">
                {form.dayType === "parent" && form.workPostcode.trim()
                  ? "Leave work at"
                  : form.dayType === "student"
                    ? "Leave school at"
                    : "Leave office at"}
              </label>
              <input
                type="time"
                value={form.leaveAtTime}
                onChange={(e) => update({ leaveAtTime: e.target.value })}
                className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope"
              />
            </div>

            {form.dayType === "parent" && (
              <>
                <div>
                  <label className="block font-manrope text-sm text-text-slate mb-2">
                    School drop-off time
                  </label>
                  <input
                    type="time"
                    value={form.schoolDropoffTime}
                    onChange={(e) =>
                      update({ schoolDropoffTime: e.target.value })
                    }
                    className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope"
                  />
                </div>
                <div>
                  <label className="block font-manrope text-sm text-text-slate mb-2">
                    School pick-up time
                  </label>
                  <input
                    type="time"
                    value={form.schoolPickupTime}
                    onChange={(e) =>
                      update({ schoolPickupTime: e.target.value })
                    }
                    className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block font-manrope text-sm text-text-slate mb-2">
                Days in office per week: {form.daysPerWeek}
              </label>
              <input
                type="range"
                min={0}
                max={5}
                value={form.daysPerWeek}
                onChange={(e) =>
                  update({ daysPerWeek: Number(e.target.value) })
                }
                className="w-full"
              />
            </div>

            <div>
              <label className="block font-manrope text-sm text-text-slate mb-2">
                Commute mode
              </label>
              <select
                value={form.commuteMode}
                onChange={(e) =>
                  update({ commuteMode: e.target.value as CommuteMode })
                }
                className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope bg-white"
              >
                {COMMUTE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section>
          <h4 className="font-teko text-xl mb-4">Extra stops (optional)</h4>
          <div className="space-y-4">
            {form.extraStops.map((stop, index) => (
              <div
                key={index}
                className="grid md:grid-cols-2 gap-4 p-4 border border-solid border-[rgba(0,0,0,0.06)]"
              >
                <div>
                  <label className="block font-manrope text-sm text-text-slate mb-2">
                    Stop label
                  </label>
                  <input
                    type="text"
                    value={stop.label}
                    onChange={(e) =>
                      updateExtraStop(index, { label: e.target.value })
                    }
                    className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope"
                    placeholder="e.g. Gym"
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <PostcodeInput
                      label="Postcode"
                      value={stop.postcode}
                      onChange={(v) => updateExtraStop(index, { postcode: v })}
                    />
                  </div>
                  {form.extraStops.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeExtraStop(index)}
                      className="mb-6 px-3 py-2 text-sm text-text-grey hover:text-red-600"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
            {form.extraStops.length < 2 && (
              <button
                type="button"
                onClick={addExtraStop}
                className="text-sm text-text-slate hover:text-text-obsidian underline"
              >
                + Add another stop
              </button>
            )}
          </div>
        </section>

        <section>
          <h4 className="font-teko text-xl mb-4">Your shops</h4>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            {SUPERMARKET_BRANDS.map((brand) => (
              <label
                key={brand.id}
                className={`flex items-center gap-2 p-3 border border-solid cursor-pointer text-sm ${
                  form.selectedSupermarketIds.includes(brand.id)
                    ? "border-brand-yellow bg-bg-porcelain"
                    : "border-[rgba(0,0,0,0.08)]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.selectedSupermarketIds.includes(brand.id)}
                  onChange={() => toggleSupermarket(brand.id)}
                />
                <span>{brand.name}</span>
                <span className="ml-auto text-xs text-text-grey capitalize">
                  {brand.tier}
                </span>
              </label>
            ))}
          </div>
          <div>
            <label className="block font-manrope text-sm text-text-slate mb-2">
              Shopping trips per week: {form.shopsPerWeek}
            </label>
            <input
              type="range"
              min={1}
              max={3}
              value={form.shopsPerWeek}
              onChange={(e) =>
                update({ shopsPerWeek: Number(e.target.value) })
              }
              className="w-full max-w-md"
            />
          </div>
        </section>
      </div>

      <button
        type="button"
        onClick={onPlanMyDay}
        disabled={isPlanning || !planEnabled}
        className="mt-8 w-full sm:w-auto px-8 py-4 bg-text-obsidian text-white font-teko text-xl uppercase disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-yellow hover:text-black transition"
      >
        {isPlanning ? "Planning your day…" : "Plan my day"}
      </button>
      {!planEnabled && (
        <p className="text-xs text-text-grey mt-2">
          Enter a valid UK postcode (e.g. W1J 8DZ) to continue.
        </p>
      )}
    </div>
  );
};

export default CostOfLivingForm;
